import { useRef, useState } from "react";
import { askResto, type AskResult } from "@/lib/askResto";
import { useShowcase } from "@/services/showcase";

const tokenKey = (tok: FilterToken) => `${tok.kind}:${tok.slug}`;
import { hasIntent, hasToken, type FilterToken, type IntentFilters } from "@/lib/feedFilter";
import type { LatLng } from "@/lib/geo";
import type { Cuisine, Tag } from "@/types/taxonomy";

interface FeedSearchContext {
  cuisines: Cuisine[];
  tags: Tag[];
  signedIn: boolean;
  coords: LatLng | null;
}

/**
 * Search + tag/cuisine filtering (Recommended tab only, fully client-side),
 * plus the "Ask Resto" concierge that turns a sentence into chips and intents.
 */
export function useFeedSearch({ cuisines, tags, signedIn, coords }: FeedSearchContext) {
  const offline = useShowcase() !== null;
  const [query, setQuery] = useState("");
  const [activeFilters, setActiveFilters] = useState<FilterToken[]>([]);
  const [filterOpen, setFilterOpen] = useState(false);
  // Scalar dimensions the concierge fills in (open now, capacity, distance,
  // offers, fast venues) — taxonomy chips stay in `activeFilters`.
  const [intent, setIntent] = useState<IntentFilters>({});
  // The last "Ask Resto" tap understood nothing — shows a one-line note until
  // the visitor edits the query.
  const [askMissed, setAskMissed] = useState(false);
  // An ask is in flight (only ever true while the server upgrade is answering —
  // the offline parse resolves in the same tick).
  const [askPending, setAskPending] = useState(false);
  // Chips/intents a real model added (AI Act Art. 50: the chip row says
  // "suggested by AI" while at least one of them is still on screen).
  const [aiKeys, setAiKeys] = useState<ReadonlySet<string>>(new Set());
  // Read inside the async ask to tell "still the sentence they asked about"
  // from "they've typed since" without re-creating the handler on every letter.
  const queryRef = useRef(query);
  queryRef.current = query;

  const changeQuery = (q: string) => {
    setQuery(q);
    setAskMissed(false);
  };
  const clearAllFilters = () => {
    setActiveFilters([]);
    setQuery("");
    setIntent({});
    setAskMissed(false);
    setAiKeys(new Set());
  };

  // "Ask Resto": parse the sentence into chips + intents. `askResto` answers
  // from the offline parser (lib/nlSearch.ts) and only reaches for the server's
  // LLM upgrade when the sentence has words it couldn't place — so the common
  // case stays instant and keyless. Two dimensions are dropped rather than
  // shown inert: capacity for guests (fail toward the guest view) and distance
  // without a position (no chip we can't honor, and no new permission prompt).
  const runAsk = async () => {
    if (askPending) return;
    const asked = query;
    setAskPending(true);
    let parsed: AskResult;
    try {
      parsed = await askResto(asked, { cuisines, tags }, { offline });
    } finally {
      setAskPending(false);
    }
    // The visitor kept typing while we waited — their edit wins over a stale answer.
    if (queryRef.current !== asked) return;
    const nextIntent: IntentFilters = { ...parsed.intent };
    if (!signedIn) delete nextIntent.capacityMax;
    if (!coords) delete nextIntent.maxDistanceKm;
    if (parsed.tokens.length === 0 && !hasIntent(nextIntent)) {
      setAskMissed(true);
      return;
    }
    setActiveFilters((prev) =>
      parsed.tokens.reduce((acc, tok) => (hasToken(acc, tok) ? acc : [...acc, tok]), prev),
    );
    setIntent((prev) => ({ ...prev, ...nextIntent }));
    setQuery(parsed.text);
    setAskMissed(false);
    if (parsed.source === "llm") {
      setAiKeys((prev) => {
        const next = new Set(prev);
        for (const tok of parsed.tokens) next.add(tokenKey(tok));
        for (const k of Object.keys(nextIntent)) next.add(`intent:${k}`);
        return next;
      });
    }
  };

  const askByAi =
    activeFilters.some((tok) => aiKeys.has(tokenKey(tok))) ||
    (Object.keys(intent) as (keyof IntentFilters)[]).some(
      (k) => intent[k] !== undefined && aiKeys.has(`intent:${k}`),
    );

  const hasFilters = query.trim() !== "" || activeFilters.length > 0 || hasIntent(intent);

  return {
    query,
    changeQuery,
    activeFilters,
    setActiveFilters,
    filterOpen,
    setFilterOpen,
    intent,
    setIntent,
    askMissed,
    askPending,
    askByAi,
    runAsk,
    clearAllFilters,
    hasFilters,
  };
}
